import React, { useState, useEffect } from 'react';
import { useUserData, usePurchaseSubscription, useSubscriptionInfo, useTransactions, useWalletData } from '../hooks/useUser';
import { 
  Box, 
  Container, 
  Stack, 
  Paper, 
  Typography, 
  Button, 
  Divider, 
  Chip,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  AccountBalanceWallet as WalletIcon,
  Stars as StarsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  ContentCopy as ContentCopyIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Use the user data query
  const { data: userData, isLoading, isError } = useUserData();

  // Use the subscription info query
  const { data: subscriptionInfo } = useSubscriptionInfo();
  
  // Use the purchase subscription mutation
  const purchaseSubscription = usePurchaseSubscription();
  
  // Use the transactions query
  const { data: transactionsData } = useTransactions({ page: 1, pageSize: 5 });
  
  // Use the wallet data query
  const { data: walletData, isLoading: isWalletLoading } = useWalletData();
  
  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [openRechargeDialog, setOpenRechargeDialog] = useState(false);
  const [openWalletAddressDialog, setOpenWalletAddressDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Copy wallet address to clipboard
  const copyToClipboard = () => {
    if (walletData && walletData.address) {
      navigator.clipboard.writeText(walletData.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/sign_in');
    }
  }, [navigate]);

  // Get subscription price based on VIP level
  const getSubscriptionPrice = () => {
    if (!userData) return 680;
    
    switch (userData.vipLevel) {
      case 1:
        return 320;
      case 3:
        return 120;
      default:
        return 680;
    }
  };
  
  // Handle purchasing subscription
  const handlePurchaseSubscription = () => {
    if (!userData) return;
    
    const price = getSubscriptionPrice();
    
    // Check if balance is sufficient
    if (parseFloat(userData.balance) < price) {
      setOpenRechargeDialog(true);
    } else {
      // Call the purchase subscription mutation
      purchaseSubscription.mutate();
      setOpenActivateDialog(false);
    }
  };


  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'None';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (isError || !userData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <Alert severity="error">
          Error loading user data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 4,
        minHeight: 'calc(100vh - 64px)', // Subtract header height
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(45deg, #303030 30%, #212121 90%)'
          : 'linear-gradient(45deg, #f5f5f5 30%, #e0e0e0 90%)',
      }}
    >
      <Container>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
          Member Dashboard
        </Typography>
        
        <Stack spacing={3}>
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={3}
          >
            {/* Member Info Card */}
            <Box sx={{ width: { xs: '100%', md: '50%' } }}>
              <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6">Member Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {userData.fullname}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {userData.email}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Member ID:</strong> {userData.id}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Status:</strong> {userData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Member Since:</strong> {formatDate(userData.createdAt)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" component="span">
                      <strong>VIP Level:</strong> 
                    </Typography>
                    <Chip 
                      icon={<StarsIcon />} 
                      label={`Level ${userData.vipLevel}`} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
            
            {/* Wallet Balance Card */}
            <Box sx={{ width: { xs: '100%', md: '50%' } }}>
              <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}>
                    <WalletIcon />
                  </Avatar>
                  <Typography variant="h6">Wallet Balance</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="h4" sx={{ mb: 3, color: theme.palette.primary.main, fontWeight: 'bold' }}>
                  {userData.balance} USDT
                </Typography>
                
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<QrCodeIcon />}
                  onClick={() => setOpenWalletAddressDialog(true)}
                  sx={{ py: 1.5 }}
                  disabled={isWalletLoading || !walletData || !walletData.address}
                >
                  {isWalletLoading ? 'Loading...' : 'Show Deposit Address'}
                </Button>
              </Paper>
            </Box>
          </Stack>
          
          {/* Transactions Card */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.info.main, mr: 2 }}>
                <HistoryIcon />
              </Avatar>
              <Typography variant="h6">Recent Transactions</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {transactionsData && transactionsData.transactions && transactionsData.transactions.length > 0 ? (
              <Box>
                {transactionsData.transactions.slice(0, 5).map((transaction: any) => (
                  <Box 
                    key={transaction.id} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{transaction.description}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body1" 
                      color={transaction.type === 'add' ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {transaction.type === 'add' ? '+' : '-'}{transaction.amount} USDT
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button variant="outlined" color="primary">
                    View All Transactions
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
                No recent transactions
              </Typography>
            )}
          </Paper>
          
          {/* Subscription Card */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                {userData.subscriptionExpiryDate ? <CheckCircleIcon /> : <CancelIcon />}
              </Avatar>
              <Typography variant="h6">Subscription</Typography>
              <Chip 
                label={userData.subscriptionExpiryDate ? "Active" : "Inactive"} 
                color={userData.subscriptionExpiryDate ? "success" : "default"}
                size="small"
                sx={{ ml: 2 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ width: { xs: '100%', md: '70%' } }}>
                <Typography variant="body1" paragraph>
                  After purchasing a subscription, you will receive the following benefits:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Dedicated customer support
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Advanced data analytics
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Priority order processing
                  </Typography>
                  <Typography component="li" variant="body2">
                    More exclusive offers
                  </Typography>
                </Box>
                
                {userData.subscriptionExpiryDate && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Expiry Date:</strong> {formatDate(userData.subscriptionExpiryDate)}
                    </Typography>
                  </Alert>
                )}
              </Box>
              <Box 
                sx={{ 
                  width: { xs: '100%', md: '30%' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => setOpenActivateDialog(true)}
                  disabled={!!userData.subscriptionExpiryDate || purchaseSubscription.isPending}
                >
                  {userData.subscriptionExpiryDate 
                    ? "Subscribed" 
                    : purchaseSubscription.isPending 
                      ? "Processing..." 
                      : "Subscribe Now"
                  }
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Stack>
        
        {/* Activation Confirmation Dialog */}
        <Dialog
          open={openActivateDialog}
          onClose={() => setOpenActivateDialog(false)}
        >
          <DialogTitle>Confirm Subscription Purchase</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Purchasing a subscription costs {getSubscriptionPrice()} USDT and is valid for 365 days. Your current balance is {userData.balance} USDT.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenActivateDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handlePurchaseSubscription} 
              color="primary" 
              variant="contained"
              disabled={purchaseSubscription.isPending}
            >
              {purchaseSubscription.isPending ? "Processing..." : "Confirm Purchase"}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Insufficient Balance Dialog */}
        <Dialog
          open={openRechargeDialog}
          onClose={() => setOpenRechargeDialog(false)}
        >
          <DialogTitle>Insufficient Balance</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your current balance is {userData.balance} USDT, which is insufficient to purchase a subscription (requires {getSubscriptionPrice()} USDT). Please deposit funds before trying again.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRechargeDialog(false)} color="primary">
              I Understand
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Wallet Address Dialog */}
        <Dialog
          open={openWalletAddressDialog}
          onClose={() => setOpenWalletAddressDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Tron USDT Deposit Address</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              {walletData && walletData.address ? (
                <QRCodeSVG 
                  value={walletData.address} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              ) : (
                <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="textSecondary">No wallet address available</Typography>
                </Box>
              )}
              
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    wordBreak: 'break-all',
                    mr: 2
                  }}
                >
                  {walletData && walletData.address ? walletData.address : 'No wallet address available'}
                </Typography>
                <Button 
                  startIcon={<ContentCopyIcon />}
                  variant="contained"
                  color={copied ? "success" : "primary"}
                  onClick={copyToClipboard}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 3, textAlign: 'center' }}>
                Please only send USDT (TRC20) to this address. Sending any other tokens may result in permanent loss.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenWalletAddressDialog(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Dashboard;
